import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Badge,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Spinner,
  Flex,
  Icon,
  HStack,
  VStack,
  Code,
  useToast,
  Divider,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  Checkbox,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import PhoneStatusBadge from "../sms/PhoneStatusBadge";
import {
  CheckCircleIcon,
  WarningIcon,
  TimeIcon,
  PhoneIcon,
  InfoIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  RepeatIcon,
  DownloadIcon,
  SearchIcon,
  CloseIcon,
  CopyIcon,
} from "@chakra-ui/icons";
import {
  getTwilioHealthWithMessages,
  testTwilioConnection,
  resendTwilioMessage,
  syncTwilioMessages,
  getTwilioMessageCount,
  getSyncProgress,
  getPatientsByPhoneBatch,
  getAllPatients,
} from "./twilioConfig";
import { getSupabase } from "../supabaseClient";
import { isInGroup } from "../../config/roles";
import { confirmBadNumbers } from "../../utils/badNumbers";

// Phone number normalization helper - strips all non-digits and removes leading 1 (country code)
// Ensures consistent phone format for lookups across SMS (E.164 format) and patients table (various formats)
const normalizePhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  // If 11 digits starting with 1 (US country code), remove it to get 10-digit local number
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  return digits;
};

const TwilioHealthStatus = () => {
  const [userRole, setUserRole] = useState(null);
  const [healthData, setHealthData] = useState({
    status: "unknown",
    lastCheck: null,
    accountSid: null,
    phoneNumber: null,
    messageCount: 0,
    errorCount: 0,
    successRate: 0,
    recentMessages: [],
    configuration: {
      accountSidConfigured: false,
      authTokenConfigured: false,
      phoneNumberConfigured: false,
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({
    total: 0,
    synced: 0,
    left: 0,
  });
  const [testResult, setTestResult] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter state for direction (sent/received) and status
  const [directionFilter, setDirectionFilter] = useState({
    sent: true,
    received: true,
  });
  const [statusFilter, setStatusFilter] = useState({
    delivered: true,
    sent: true,
    failed: true,
    undelivered: true,
    received: true,
  });

  // Force refresh key to ensure filter changes trigger re-render
  const [, setRefreshKey] = useState(0);

  // Store all messages for filtering when filters change
  const [allMessages, setAllMessages] = useState([]);

  // Patient lookup cache for phone -> patient name
  const [patientLookup, setPatientLookup] = useState({});

  const progressIntervalRef = React.useRef(null);
  const toast = useToast();

  // Resend Modal State
  const {
    isOpen: isResendOpen,
    onOpen: onResendOpen,
    onClose: onResendClose,
  } = useDisclosure();
  // Sync Report Modal State
  const {
    isOpen: isSyncReportOpen,
    onOpen: onSyncReportOpen,
    onClose: onSyncReportClose,
  } = useDisclosure();
  // Message View Modal State
  const {
    isOpen: isMessageViewOpen,
    onOpen: onMessageViewOpen,
    onClose: onMessageViewClose,
  } = useDisclosure();

  const [syncReport, setSyncReport] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [viewingMessage, setViewingMessage] = useState(null);
  const [resendPhoneNumber, setResendPhoneNumber] = useState("");
  const [resendMessageBody, setResendMessageBody] = useState("");
  const [resendMessageId, setResendMessageId] = useState(null); // Store the SMS ID for deletion on resend
  const [shouldSplit, setShouldSplit] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [copied, setCopied] = useState(false);

  const checkTwilioHealth = async (
    page = 0,
    query = searchQuery,
    getAllMessages = false,
  ) => {
    setIsLoading(true);
    try {
      // Fetch complete health data including message history with pagination or all messages
      const healthData = await getTwilioHealthWithMessages(
        page,
        query,
        getAllMessages,
      );

      setHealthData((prev) => ({
        ...prev,
        ...healthData,
        lastCheck: new Date().toISOString(),
      }));

      // Store all messages when fetching all (for filtering)
      if (getAllMessages && healthData.recentMessages) {
        setAllMessages(healthData.recentMessages);
      }

      // Fetch patient names for all unique phone numbers
      if (healthData.recentMessages && healthData.recentMessages.length > 0) {
        const uniquePhones = [...new Set(
          healthData.recentMessages.map(msg => msg.phoneNumber).filter(Boolean)
        )];
        
        console.log('[SMS DEBUG] Patient lookup - unique phones found:', uniquePhones.length);
        console.log('[SMS DEBUG] Patient lookup - phones:', JSON.stringify(uniquePhones));
        
        // Normalize phones for consistent lookup
        const normalizedUniquePhones = uniquePhones.map(p => normalizePhone(p));
        console.log('[SMS DEBUG] Patient lookup - normalized phones:', JSON.stringify(normalizedUniquePhones));
        
        // Only fetch if we have phones and they haven't all been looked up already
        const phonesNeedingLookup = normalizedUniquePhones.filter(phone => !patientLookup[phone]);
        console.log('[SMS DEBUG] Patient lookup - phones needing lookup:', phonesNeedingLookup.length);
        
        if (phonesNeedingLookup.length > 0) {
          try {
            console.log('[SMS DEBUG] Calling getPatientsByPhoneBatch with normalized phones:', JSON.stringify(phonesNeedingLookup));
            const patientData = await getPatientsByPhoneBatch(phonesNeedingLookup);
            console.log('[SMS DEBUG] getPatientsByPhoneBatch returned:', JSON.stringify(Object.keys(patientData)));
            if (Object.keys(patientData).length > 0) {
              // Store with NORMALIZED phone keys for consistent lookup
              const normalizedPatientData = {};
              for (const [phone, patient] of Object.entries(patientData)) {
                const normalizedPhone = normalizePhone(phone);
                normalizedPatientData[normalizedPhone] = patient;
                console.log(`[SMS DEBUG] Storing patient lookup: normalizePhone("${phone}") -> "${normalizedPhone}" = "${patient.name}"`);
              }
              console.log('[SMS DEBUG] Setting patientLookup with normalized keys:', JSON.stringify(Object.keys(normalizedPatientData)));
              setPatientLookup(prev => ({ ...prev, ...normalizedPatientData }));
            } else {
              console.log('[SMS DEBUG] No patients found for any phone number');
            }
          } catch (patientError) {
            console.warn("Could not fetch patient data:", patientError);
          }
        } else {
          console.log('[SMS DEBUG] All phones already in patientLookup cache');
        }
      }

      setCurrentPage(page);

      // Only show toast if not searching/filtering and user is not freemium, to avoid spamming
      if (!query && !isInGroup(userRole, 'freemium')) {
        toast({
          title: "Health Check Complete",
          description: `Loaded ${
            healthData.recentMessages?.length || 0
          } messages (Page ${page + 1} of ${healthData.totalPages || 1})`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Health check failed:", error);

      setHealthData((prev) => ({
        ...prev,
        status: "error",
        lastCheck: new Date().toISOString(),
      }));

      toast({
        title: "Health Check Failed",
        description: "Unable to fetch Twilio health data and message history",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const goToPage = (page) => {
    // When filters are active, use filteredStats for pagination
    const totalPages =
      allMessages.length > 0
        ? filteredStats.totalFilteredPages
        : healthData.totalPages || 1;

    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
      // If not using all messages, fetch the page from server
      if (allMessages.length === 0) {
        checkTwilioHealth(page);
      }
      // Otherwise, the filteredMessages useMemo will handle pagination locally
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    const totalPages =
      allMessages.length > 0
        ? filteredStats.totalFilteredPages
        : healthData.totalPages || 1;

    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1);
    }
  };

  const testConnection = async () => {
    setIsTesting(true);
    setTestResult(null);

    try {
      const testResult = await testTwilioConnection();
      setTestResult(testResult);

      const configComplete = testResult.success;

      toast({
        title: configComplete
          ? "Configuration Valid"
          : "Configuration Incomplete",
        description: configComplete
          ? "All Twilio credentials are properly configured"
          : "Some Twilio credentials are missing or invalid",
        status: configComplete ? "success" : "warning",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Test failed:", error);

      setTestResult({
        success: false,
        message: error.message || "Connection test failed",
        timestamp: new Date().toISOString(),
      });

      toast({
        title: "Test Failed",
        description: error.message || "Failed to test Twilio connection",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSearch = () => {
    checkTwilioHealth(0, searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    checkTwilioHealth(0, "");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleResendClick = (message) => {
    setSelectedMessage(message);
    setResendPhoneNumber(message.phoneNumber);
    setResendMessageBody(message.fullMessage);
    setResendMessageId(message.id); // Store the SMS ID for deletion on resend

    // Auto-enable split for messages that are likely too long or have length-related errors
    const messageText = message.fullMessage || message.preview || "";
    const isLengthError =
      message.errorCode === "30019" || // Content size exceeds carrier limit
      message.errorCode === "21617"; // Message body is too long (Twilio)
    const isVeryLong = messageText.length > 160; // Over 1 standard SMS segment

    setShouldSplit(isLengthError || isVeryLong);
    onResendOpen();
  };

  const handlePreviewClick = (message) => {
    setViewingMessage(message);
    setCopied(false);
    onMessageViewOpen();
  };

  const handleCopyMessage = async () => {
    if (!viewingMessage) return;

    const messageText =
      viewingMessage.fullMessage || viewingMessage.preview || "";

    try {
      await navigator.clipboard.writeText(messageText);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Message content copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast({
        title: "Copy Failed",
        description: "Failed to copy message to clipboard",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleResendConfirm = async () => {
    if (!resendPhoneNumber || !resendMessageBody) {
      toast({
        title: "Validation Error",
        description: "Phone number and message body are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const badNumberGuard = await confirmBadNumbers([{ phone: resendPhoneNumber }]);
    if (!badNumberGuard) return;

    setIsResending(true);
    try {
      const result = await resendTwilioMessage(
        resendPhoneNumber,
        resendMessageBody,
        shouldSplit,
        null, // scheduleDate
        resendMessageId, // Pass the old SMS ID to delete the failed record
        null, // appointmentDate
        null, // schema
        badNumberGuard.confirmed(resendPhoneNumber),
      );

      toast({
        title: "Message Resent",
        description: "The message has been successfully resent.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onResendClose();
      // Refresh the list to show the new message
      checkTwilioHealth(currentPage);
    } catch (error) {
      console.error("Resend failed:", error);
      toast({
        title: "Resend Failed",
        description: error.message || "Failed to resend the message.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleSyncClick = async () => {
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    setIsSyncing(true);
    setSyncProgress({ total: 0, synced: 0, left: 0 });
    let syncId = null;

    try {
      toast({
        title: "Sync Started",
        description: "Getting message count...",
        status: "info",
        duration: null,
        isClosable: false,
        id: "sync-toast",
      });

      // First, get the total message count from Twilio
      const totalCount = await getTwilioMessageCount(true);

      // Update progress with total count immediately
      setSyncProgress({
        total: totalCount,
        synced: 0,
        left: totalCount,
      });

      // Update toast with count
      if (toast.isActive("sync-toast")) {
        toast.close("sync-toast");
      }

      toast({
        title: "Syncing Messages",
        description: `Synchronizing ${totalCount} messages from Twilio. This may take a while...`,
        status: "info",
        duration: null,
        isClosable: false,
        id: "sync-toast",
      });

      // Start the actual sync (this returns a sync_id immediately)
      const result = await syncTwilioMessages(true);

      console.log("Sync result:", result);

      syncId = result.sync_id || result.syncId;

      if (!syncId) {
        // Check if configuration is complete
        const config = result.configuration || {};
        const isConfigComplete =
          config.accountSidConfigured &&
          config.authTokenConfigured &&
          config.phoneNumberConfigured;

        if (!isConfigComplete) {
          throw new Error(
            "Twilio configuration is incomplete. Please configure all Twilio credentials first.",
          );
        }

        // If sync was requested but no sync_id returned, log details
        console.error("Sync failed - no sync_id returned. Result:", result);
        throw new Error(
          `Failed to get sync ID from server. Status: ${result.status || "unknown"}, Error: ${result.error || "Unknown error"}`,
        );
      }

      // Start polling for progress immediately
      progressIntervalRef.current = setInterval(async () => {
        try {
          const progress = await getSyncProgress(syncId);
          if (progress) {
            setSyncProgress({
              total: progress.total || 0,
              synced: progress.synced || 0,
              left: progress.left || 0,
            });

            // If sync is complete, stop polling
            if (progress.is_complete) {
              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }

              // Update with final results including sync report
              if (progress.sync_report) {
                setSyncReport(progress.sync_report);
              }

              // Update toast and refresh
              if (toast.isActive("sync-toast")) {
                toast.close("sync-toast");
              }

              toast({
                title: "Sync Complete",
                description: `Successfully synchronized ${progress.synced} SMS statuses from Twilio.`,
                status: "success",
                duration: 3000,
                isClosable: true,
              });

              if (progress.sync_report && progress.sync_report.length > 0) {
                setSyncReport(progress.sync_report);
                onSyncReportOpen();
              }

              // Refresh the view
              checkTwilioHealth(currentPage);

              setIsSyncing(false);
            }
          }
        } catch (error) {
          console.error("Error polling sync progress:", error);
        }
      }, 500); // Poll every 500ms

      // Also set a timeout to stop polling after 5 minutes (safety)
      setTimeout(
        () => {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
            setIsSyncing(false);
          }
        },
        5 * 60 * 1000,
      );

      // Store interval in a way that cleanup can access it
      // The interval will handle completion and set isSyncing to false
    } catch (error) {
      console.error("Sync failed:", error);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (toast.isActive("sync-toast")) {
        toast.close("sync-toast");
      }
      setSyncProgress({ total: 0, synced: 0, left: 0 });
      setIsSyncing(false);
      toast({
        title: "Sync Failed",
        description:
          error.message || "Failed to synchronize messages with Twilio.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    // Note: We don't set isSyncing to false in finally because
    // polling continues asynchronously and will set it to false when complete
  };

  // Fetch user role on component mount, then run health check
  useEffect(() => {
    const fetchUserRoleAndCheckHealth = async () => {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', session.user.id)
          .single();
        if (data && !error) {
          setUserRole(data.role);
        }
      }
      
      // OPTIMIZATION: Start patient preload and health check in PARALLEL
      // This significantly improves initial load time
      
      // Preload patients (with caching - subsequent loads are instant)
      const preloadPatients = async () => {
        try {
          console.log('[SMS DEBUG] Preloading all patients...');
          const allPatients = await getAllPatients();
          console.log('[SMS DEBUG] Preloaded', allPatients.length, 'patients');
          
          if (allPatients.length > 0) {
            // Build patientLookup map: phone -> patient info
            // Use NORMALIZED phones as keys to ensure consistent lookups
            // SMS messages have phones in E.164 format (+13236132761) but patients table has various formats
            const lookupMap = {};
            for (const patient of allPatients) {
              if (patient.phone) {
                // Normalize patient phone for consistent lookup
                const normalizedPatientPhone = normalizePhone(patient.phone);
                
                // Store using NORMALIZED phone as key (this is how SMS messages store phones too after normalization)
                lookupMap[normalizedPatientPhone] = { name: patient.name, id: patient.id };
                
                // DEBUG: Log sample normalization
                if (Object.keys(lookupMap).length <= 5) {
                  console.log(`[SMS DEBUG] Patient phone normalization: "${patient.phone}" -> "${normalizedPatientPhone}"`);
                }
              }
            }
            console.log('[SMS DEBUG] Setting patientLookup with', Object.keys(lookupMap).length, 'entries (normalized keys)');
            setPatientLookup(lookupMap);
            return true;
          }
        } catch (preloadErr) {
          console.warn('[SMS DEBUG] Patient preloading failed:', preloadErr);
        }
        return false;
      };
      
      // Run both in parallel - health check will wait for patient data if needed when displaying names
      // Use Promise.all to start both simultaneously
      await Promise.all([
        preloadPatients(),
        checkTwilioHealth()
      ]);
    };

    fetchUserRoleAndCheckHealth();

    // Cleanup: stop progress polling and reset all messages on unmount
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      // Reset all messages to force server-side pagination on next mount
      setAllMessages([]);
      setCurrentPage(0);
    };
  }, []);

  const getStatusBadge = (status) => {
    const statusConfig = {
      healthy: { color: "green", text: "Healthy", icon: CheckCircleIcon },
      warning: { color: "yellow", text: "Warning", icon: WarningIcon },
      error: { color: "red", text: "Error", icon: WarningIcon },
      unknown: { color: "gray", text: "Unknown", icon: TimeIcon },
    };

    const config = statusConfig[status] || statusConfig.unknown;
    const IconComponent = config.icon;

    return (
      <Badge colorScheme={config.color} px={3} py={1} borderRadius="md">
        <HStack spacing={1}>
          <Icon as={IconComponent} />
          <Text>{config.text}</Text>
        </HStack>
      </Badge>
    );
  };

  const getConfigurationStatus = () => {
    const { configuration } = healthData;
    const total = Object.keys(configuration).length;
    const configured = Object.values(configuration).filter(Boolean).length;
    const percentage = (configured / total) * 100;

    return {
      configured,
      total,
      percentage,
      isComplete: configured === total,
    };
  };

  const configStatus = getConfigurationStatus();

  // Compute filtered messages based on direction and status filters
  // Uses allMessages when available (after filter change), otherwise falls back to recentMessages
  const filteredMessages = React.useMemo(() => {
    // Use all messages if available, otherwise use the paginated recentMessages
    const messagesToFilter =
      allMessages.length > 0 ? allMessages : healthData.recentMessages;

    if (!messagesToFilter || messagesToFilter.length === 0) return [];

    // First filter by direction and status
    let filtered = messagesToFilter.filter((message) => {
      // Filter by direction
      const isOutbound = message.direction === "outbound";
      const isInbound =
        message.direction === "inbound" || message.direction === "received";

      if (isOutbound && !directionFilter.sent) return false;
      if (isInbound && !directionFilter.received) return false;

      // Filter by status (normalize status for comparison)
      const normalizedStatus = message.status?.toLowerCase();
      if (normalizedStatus === "delivered" && !statusFilter.delivered)
        return false;
      if (normalizedStatus === "sent" && !statusFilter.sent) return false;
      if (normalizedStatus === "failed" && !statusFilter.failed) return false;
      if (normalizedStatus === "undelivered" && !statusFilter.undelivered)
        return false;
      if (normalizedStatus === "received" && !statusFilter.received)
        return false;

      return true;
    });

    // Sort by timestamp (most recent first) - already sorted from API but ensure it
    filtered.sort((a, b) => {
      const dateA = new Date(a.timestamp || a.createdAt || 0);
      const dateB = new Date(b.timestamp || b.createdAt || 0);
      return dateB - dateA; // Descending order (most recent first)
    });

    // If using all messages, paginate the results (10 per page)
    if (allMessages.length > 0) {
      const pageSize = 10;
      const startIndex = currentPage * pageSize;
      filtered = filtered.slice(startIndex, startIndex + pageSize);
    }

    return filtered;
  }, [
    allMessages,
    healthData.recentMessages,
    directionFilter,
    statusFilter,
    currentPage,
    patientLookup, // Must include to re-render when patient data is loaded
  ]);

  // Compute total filtered count and pages for pagination display
  const filteredStats = React.useMemo(() => {
    const messagesToCount =
      allMessages.length > 0 ? allMessages : healthData.recentMessages;

    if (!messagesToCount || messagesToCount.length === 0) {
      return { totalFiltered: 0, totalFilteredPages: 0 };
    }

    let totalFiltered = messagesToCount.filter((message) => {
      const isOutbound = message.direction === "outbound";
      const isInbound =
        message.direction === "inbound" || message.direction === "received";

      if (isOutbound && !directionFilter.sent) return false;
      if (isInbound && !directionFilter.received) return false;

      const normalizedStatus = message.status?.toLowerCase();
      if (normalizedStatus === "delivered" && !statusFilter.delivered)
        return false;
      if (normalizedStatus === "sent" && !statusFilter.sent) return false;
      if (normalizedStatus === "failed" && !statusFilter.failed) return false;
      if (normalizedStatus === "undelivered" && !statusFilter.undelivered)
        return false;
      if (normalizedStatus === "received" && !statusFilter.received)
        return false;

      return true;
    }).length;

    const totalFilteredPages = Math.ceil(totalFiltered / 10);

    return { totalFiltered, totalFilteredPages };
  }, [allMessages, healthData.recentMessages, directionFilter, statusFilter]);

  // Handler functions for filter changes - fetch all messages and filter locally
  const handleDirectionChange = async (type) => {
    const newDirectionFilter = {
      ...directionFilter,
      [type]: !directionFilter[type],
    };
    setDirectionFilter(newDirectionFilter);

    // Reset to first page and fetch all messages for proper filtering
    setCurrentPage(0);
    await checkTwilioHealth(0, searchQuery, true);
  };

  const handleStatusChange = async (status) => {
    const newStatusFilter = {
      ...statusFilter,
      [status]: !statusFilter[status],
    };
    setStatusFilter(newStatusFilter);

    // Reset to first page and fetch all messages for proper filtering
    setCurrentPage(0);
    await checkTwilioHealth(0, searchQuery, true);
  };

  return (
    <Box maxW="1200px" mx="auto" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Card>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <VStack align="start" spacing={2}>
                <Heading size="lg">Twilio API Health Status</Heading>
                <Text color="gray.600">
                  Monitor your Twilio SMS service configuration and performance
                </Text>
              </VStack>
              <VStack align="end" spacing={2}>
                {getStatusBadge(healthData.status)}
                <Text fontSize="sm" color="gray.500">
                  Last checked:{" "}
                  {healthData.lastCheck
                    ? new Date(healthData.lastCheck).toLocaleString()
                    : "Never"}
                </Text>
              </VStack>
            </Flex>
          </CardHeader>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={4}>
                <Button
                  colorScheme="blue"
                  onClick={() => checkTwilioHealth(currentPage)}
                  isLoading={isLoading}
                  leftIcon={<RepeatIcon />}
                >
                  Refresh Status
                </Button>
                <Button
                  colorScheme="purple"
                  onClick={handleSyncClick}
                  isLoading={isSyncing}
                  leftIcon={<DownloadIcon />}
                  isDisabled={isSyncing}
                >
                  Sync Messages
                </Button>
                <Button
                  colorScheme="green"
                  onClick={testConnection}
                  isLoading={isTesting}
                  leftIcon={<PhoneIcon />}
                >
                  Test Connection
                </Button>
              </HStack>

              {/* Sync Progress Bar */}
              {isSyncing && (
                <Box>
                  <Flex justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="semibold">
                      Syncing messages...
                    </Text>
                    {syncProgress.total > 0 ? (
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">
                        {syncProgress.left.toLocaleString()} messages remaining
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="gray.600">
                        Getting message count...
                      </Text>
                    )}
                  </Flex>
                  <Progress
                    value={
                      syncProgress.total > 0
                        ? ((syncProgress.total - syncProgress.left) /
                            syncProgress.total) *
                          100
                        : 0
                    }
                    colorScheme="purple"
                    size="lg"
                    borderRadius="md"
                    isIndeterminate={syncProgress.total === 0}
                  />
                  {syncProgress.total > 0 && (
                    <Flex justify="space-between" mt={1}>
                      <Text fontSize="xs" color="gray.500">
                        {syncProgress.synced.toLocaleString()} of{" "}
                        {syncProgress.total.toLocaleString()} synced
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {Math.round(
                          ((syncProgress.total - syncProgress.left) /
                            syncProgress.total) *
                            100,
                        )}
                        % complete
                      </Text>
                    </Flex>
                  )}
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Configuration Status */}
        <Card>
          <CardHeader>
            <Heading size="md">Configuration Status</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Flex justify="space-between" mb={2}>
                  <Text fontWeight="semibold">Configuration Progress</Text>
                  <Text>
                    {configStatus.configured}/{configStatus.total} configured
                  </Text>
                </Flex>
                <Progress
                  value={configStatus.percentage}
                  colorScheme={configStatus.isComplete ? "green" : "yellow"}
                  size="lg"
                  borderRadius="md"
                />
              </Box>

              <SimpleGrid columns={[1, 2]} spacing={4}>
                <Box p={4} borderWidth={1} borderRadius="md">
                  <Text fontWeight="semibold" mb={2}>
                    Account SID
                  </Text>
                  <HStack>
                    {healthData.configuration.accountSidConfigured ? (
                      <CheckCircleIcon color="green.500" />
                    ) : (
                      <WarningIcon color="red.500" />
                    )}
                    <Text>
                      {healthData.configuration.accountSidConfigured
                        ? "Configured"
                        : "Missing"}
                    </Text>
                  </HStack>
                </Box>

                <Box p={4} borderWidth={1} borderRadius="md">
                  <Text fontWeight="semibold" mb={2}>
                    Auth Token
                  </Text>
                  <HStack>
                    {healthData.configuration.authTokenConfigured ? (
                      <CheckCircleIcon color="green.500" />
                    ) : (
                      <WarningIcon color="red.500" />
                    )}
                    <Text>
                      {healthData.configuration.authTokenConfigured
                        ? "Configured"
                        : "Missing"}
                    </Text>
                  </HStack>
                </Box>

                <Box p={4} borderWidth={1} borderRadius="md">
                  <Text fontWeight="semibold" mb={2}>
                    Phone Number
                  </Text>
                  <HStack>
                    {healthData.configuration.phoneNumberConfigured ? (
                      <CheckCircleIcon color="green.500" />
                    ) : (
                      <WarningIcon color="red.500" />
                    )}
                    <Text>
                      {healthData.configuration.phoneNumberConfigured
                        ? "Configured"
                        : "Missing"}
                    </Text>
                  </HStack>
                </Box>

                <Box p={4} borderWidth={1} borderRadius="md">
                  <Text fontWeight="semibold" mb={2}>
                    Twilio Phone
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {healthData.phoneNumber || "Not available"}
                  </Text>
                </Box>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>

        {/* Statistics */}
        <Card>
          <CardHeader>
            <Heading size="md">Message Statistics</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={[1, 2, 4]} spacing={6}>
              <Stat>
                <StatLabel>Total Messages</StatLabel>
                <StatNumber>{healthData.messageCount}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  Last 24 hours
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>Failed Messages</StatLabel>
                <StatNumber color="red.500">{healthData.errorCount}</StatNumber>
                <StatHelpText>
                  <StatArrow type="decrease" />
                  Last 24 hours
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>Success Rate</StatLabel>
                <StatNumber color="green.500">
                  {healthData.successRate}%
                </StatNumber>
                <StatHelpText>24-hour average</StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>Account SID</StatLabel>
                <StatNumber fontSize="md">
                  {healthData.accountSid || "Not configured"}
                </StatNumber>
                <StatHelpText>Partial identifier</StatHelpText>
              </Stat>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Message History & Search */}
        <Card>
          <CardHeader>
            <Flex
              justify="space-between"
              align="center"
              direction={{ base: "column", md: "row" }}
              gap={4}
            >
              <Flex align="center" gap={4}>
                <Heading size="md">Recent SMS Messages</Heading>
                {healthData.recentMessages && (
                  <HStack display={{ base: "none", lg: "flex" }}>
                    <Badge colorScheme="blue" variant="outline">
                      {allMessages.length > 0
                        ? `${filteredStats.totalFiltered} filtered`
                        : healthData.totalMessageCount ||
                          healthData.recentMessages?.length}{" "}
                      messages
                    </Badge>
                    {(allMessages.length > 0
                      ? filteredStats.totalFilteredPages > 1
                      : healthData.totalPages > 1) && (
                      <Text fontSize="sm" color="gray.500">
                        Page {currentPage + 1} of{" "}
                        {allMessages.length > 0
                          ? filteredStats.totalFilteredPages
                          : healthData.totalPages}
                      </Text>
                    )}
                    <Tooltip label="Messages sent and received through Twilio SMS service">
                      <InfoIcon color="gray.500" />
                    </Tooltip>
                  </HStack>
                )}
              </Flex>

              <Box minW={{ base: "100%", md: "300px" }}>
                <InputGroup size="sm">
                  <InputLeftElement
                    pointerEvents="none"
                    children={<SearchIcon color="gray.300" />}
                  />
                  <Input
                    placeholder="Search phone number or message..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    borderRadius="md"
                  />
                  <InputRightElement width={searchQuery ? "4.5rem" : "2rem"}>
                    {searchQuery ? (
                      <IconButton
                        h="1.75rem"
                        size="sm"
                        icon={<CloseIcon />}
                        onClick={handleClearSearch}
                        variant="ghost"
                        aria-label="Clear search"
                        mr={1}
                      />
                    ) : null}
                  </InputRightElement>
                </InputGroup>
              </Box>
            </Flex>

            {/* Filter Checkboxes */}
            <Flex
              gap={6}
              wrap="wrap"
              align="center"
              pt={2}
              pb={2}
              pl={2}
              pr={2}
              bg="gray.50"
              borderRadius="md"
              borderWidth={1}
              borderColor="gray.200"
            >
              <Text fontSize="sm" fontWeight="semibold" mr={2}>
                Filters:
              </Text>

              {/* Direction Filters */}
              <HStack spacing={4}>
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  Direction:
                </Text>
                <Checkbox
                  size="sm"
                  isChecked={directionFilter.sent}
                  onChange={() => handleDirectionChange("sent")}
                  colorScheme="blue"
                >
                  <Text fontSize="xs">📤 Sent</Text>
                </Checkbox>
                <Checkbox
                  size="sm"
                  isChecked={directionFilter.received}
                  onChange={() => handleDirectionChange("received")}
                  colorScheme="green"
                >
                  <Text fontSize="xs">📥 Received</Text>
                </Checkbox>
              </HStack>

              <Divider orientation="vertical" h={5} />

              {/* Status Filters */}
              <HStack spacing={3} flexWrap="wrap">
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  Status:
                </Text>
                <Checkbox
                  size="sm"
                  isChecked={statusFilter.delivered}
                  onChange={() => handleStatusChange("delivered")}
                  colorScheme="green"
                >
                  <Text fontSize="xs">Delivered</Text>
                </Checkbox>
                <Checkbox
                  size="sm"
                  isChecked={statusFilter.sent}
                  onChange={() => handleStatusChange("sent")}
                  colorScheme="blue"
                >
                  <Text fontSize="xs">Sent</Text>
                </Checkbox>
                <Checkbox
                  size="sm"
                  isChecked={statusFilter.failed}
                  onChange={() => handleStatusChange("failed")}
                  colorScheme="red"
                >
                  <Text fontSize="xs">Failed</Text>
                </Checkbox>
                <Checkbox
                  size="sm"
                  isChecked={statusFilter.undelivered}
                  onChange={() => handleStatusChange("undelivered")}
                  colorScheme="orange"
                >
                  <Text fontSize="xs">Undelivered</Text>
                </Checkbox>
                <Checkbox
                  size="sm"
                  isChecked={statusFilter.received}
                  onChange={() => handleStatusChange("received")}
                  colorScheme="purple"
                >
                  <Text fontSize="xs">Received</Text>
                </Checkbox>
              </HStack>

              {/* Show count of filtered messages */}
              <Text fontSize="xs" color="gray.500" ml="auto">
                {allMessages.length > 0
                  ? `Showing ${filteredMessages.length} of ${filteredStats.totalFiltered} filtered messages`
                  : `Showing ${filteredMessages.length} of ${healthData.recentMessages?.length || 0} messages`}
              </Text>
            </Flex>
          </CardHeader>
          <CardBody>
            {filteredMessages && filteredMessages.length > 0 ? (
              <>
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Time</Th>
                        <Th>Direction</Th>
                        <Th>Phone Number</Th>
                        <Th>Patient</Th>
                        <Th>Preview</Th>
                        <Th>Status</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredMessages.map((message, index) => (
                        <Tr key={message.id || index}>
                          <Td fontSize="sm">
                            {new Date(
                              message.timestamp || message.createdAt,
                            ).toLocaleString()}
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={
                                message.direction === "outbound"
                                  ? "blue"
                                  : "green"
                              }
                              variant="outline"
                            >
                              {message.direction === "outbound"
                                ? "📤 Sent"
                                : "📥 Received"}
                            </Badge>
                          </Td>
                          <Td fontSize="sm" fontFamily="mono">
                            {message.phoneNumber}
                            <PhoneStatusBadge phone={message.phoneNumber} compact ml={1} fontFamily="body" />
                          </Td>
                          <Td fontSize="sm">
                            {message.patientName ? (
                              <Text fontWeight="medium" color="gray.700">
                                {message.patientName}
                              </Text>
                            ) : patientLookup[normalizePhone(message.phoneNumber)]?.name ? (
                              <Text fontWeight="medium" color="gray.700">
                                {patientLookup[normalizePhone(message.phoneNumber)].name}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                                Unknown
                              </Text>
                            )}
                          </Td>
                          <Td maxW="300px">
                            {message.status === "failed" ||
                            message.status === "Failed" ||
                            message.status === "undelivered" ||
                            message.errorMessage ||
                            message.errorCode ? (
                              // Show full error details for failed messages - clickable
                              <Tooltip
                                label="Click to view full message and error details"
                                hasArrow
                              >
                                <Box
                                  cursor="pointer"
                                  onClick={() => handlePreviewClick(message)}
                                  _hover={{ opacity: 0.8 }}
                                >
                                  <Text
                                    fontSize="sm"
                                    color="red.600"
                                    fontWeight="medium"
                                    mb={1}
                                  >
                                    Error Details:
                                  </Text>
                                  {message.errorCode && (
                                    <Text fontSize="xs" color="red.500" mb={1}>
                                      Code: {message.errorCode}
                                    </Text>
                                  )}
                                  <Text
                                    fontSize="sm"
                                    color="red.600"
                                    whiteSpace="pre-wrap"
                                    wordBreak="break-word"
                                    maxH="150px"
                                    overflowY="auto"
                                  >
                                    {message.errorMessage ||
                                      "No error message available"}
                                  </Text>
                                </Box>
                              </Tooltip>
                            ) : (
                              // Show normal message preview for successful messages - clickable
                              <Tooltip
                                label="Click to view full message"
                                hasArrow
                              >
                                <Text
                                  fontSize="sm"
                                  isTruncated
                                  cursor="pointer"
                                  color="blue.600"
                                  _hover={{
                                    color: "blue.800",
                                    textDecoration: "underline",
                                  }}
                                  onClick={() => handlePreviewClick(message)}
                                >
                                  {message.preview}
                                </Text>
                              </Tooltip>
                            )}
                          </Td>
                          <Td>
                            <Tooltip
                              label={
                                message.errorMessage ||
                                (message.errorCode
                                  ? `Error Code: ${message.errorCode}`
                                  : null)
                              }
                              isDisabled={
                                !message.errorMessage && !message.errorCode
                              }
                              hasArrow
                            >
                              <Badge
                                colorScheme={
                                  message.status === "delivered" ||
                                  message.status === "received" ||
                                  message.status === "Success"
                                    ? "green"
                                    : message.status === "failed" ||
                                        message.status === "Failed" ||
                                        message.status === "undelivered"
                                      ? "red"
                                      : message.status === "sent" ||
                                          message.status === "outbound"
                                        ? "blue"
                                        : "gray"
                                }
                                variant="subtle"
                                cursor={
                                  message.errorMessage || message.errorCode
                                    ? "help"
                                    : "default"
                                }
                              >
                                {message.status}
                                {message.errorMessage || message.errorCode ? (
                                  <WarningIcon ml={1} boxSize={3} />
                                ) : null}
                              </Badge>
                            </Tooltip>
                          </Td>
                          <Td>
                            {(message.status === "failed" ||
                              message.status === "Failed" ||
                              message.status === "undelivered") && (
                              <Button
                                size="xs"
                                leftIcon={<RepeatIcon />}
                                colorScheme="red"
                                variant="outline"
                                onClick={() => handleResendClick(message)}
                              >
                                Resend
                              </Button>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>

                {/* Pagination Controls */}
                {(allMessages.length > 0
                  ? filteredStats.totalFilteredPages > 1
                  : healthData.totalPages > 1) && (
                  <Flex justify="space-between" align="center" mt={4} px={2}>
                    <Text fontSize="sm" color="gray.500">
                      {allMessages.length > 0
                        ? `Page ${currentPage + 1} of ${filteredStats.totalFilteredPages} (${filteredStats.totalFiltered} total filtered)`
                        : `Showing ${filteredMessages.length} of ${healthData.totalMessageCount} messages`}
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        leftIcon={<ChevronLeftIcon />}
                        onClick={goToPreviousPage}
                        isDisabled={currentPage === 0}
                        variant="outline"
                      >
                        Previous
                      </Button>
                      <HStack spacing={1}>
                        {Array.from(
                          {
                            length: Math.min(
                              allMessages.length > 0
                                ? filteredStats.totalFilteredPages
                                : healthData.totalPages,
                              5,
                            ),
                          },
                          (_, i) => {
                            const totalPages =
                              allMessages.length > 0
                                ? filteredStats.totalFilteredPages
                                : healthData.totalPages;
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i;
                            } else if (currentPage < 3) {
                              pageNum = i;
                            } else if (currentPage > totalPages - 3) {
                              pageNum = totalPages - 5 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                size="xs"
                                variant={
                                  currentPage === pageNum ? "solid" : "outline"
                                }
                                colorScheme={
                                  currentPage === pageNum ? "blue" : "gray"
                                }
                                onClick={() => goToPage(pageNum)}
                              >
                                {pageNum + 1}
                              </Button>
                            );
                          },
                        )}
                      </HStack>
                      <Button
                        size="sm"
                        rightIcon={<ChevronRightIcon />}
                        onClick={goToNextPage}
                        isDisabled={
                          currentPage >=
                          (allMessages.length > 0
                            ? filteredStats.totalFilteredPages - 1
                            : healthData.totalPages - 1)
                        }
                        variant="outline"
                      >
                        Next
                      </Button>
                    </HStack>
                  </Flex>
                )}

                {/* Fallback for single page */}
                {(allMessages.length > 0
                  ? filteredStats.totalFilteredPages <= 1
                  : healthData.totalPages <= 1) &&
                  filteredMessages?.length >= 10 && (
                    <Box mt={4} textAlign="center">
                      <Text fontSize="sm" color="gray.500">
                        {allMessages.length > 0
                          ? `Showing ${filteredMessages.length} of ${filteredStats.totalFiltered} filtered messages`
                          : `Showing ${filteredMessages.length} of ${healthData.totalMessageCount} messages`}
                      </Text>
                    </Box>
                  )}
              </>
            ) : (
              <Box textAlign="center" py={8}>
                {/* Check if any filters are active */}
                {(() => {
                  const hasFiltersActive =
                    searchQuery ||
                    !directionFilter.sent ||
                    !directionFilter.received ||
                    !statusFilter.delivered ||
                    !statusFilter.sent ||
                    !statusFilter.failed ||
                    !statusFilter.undelivered ||
                    !statusFilter.received;

                  if (hasFiltersActive) {
                    return (
                      <>
                        <SearchIcon boxSize={12} color="gray.300" mb={4} />
                        <Heading size="md" color="gray.500" mb={2}>
                          No messages match your filters
                        </Heading>
                        <Text color="gray.400">
                          Try adjusting your search query or filter criteria.
                        </Text>
                        <HStack mt={4} justify="center" spacing={2}>
                          {searchQuery && (
                            <Button
                              onClick={handleClearSearch}
                              size="sm"
                              variant="outline"
                            >
                              Clear Search
                            </Button>
                          )}
                          <Button
                            onClick={() => {
                              setDirectionFilter({
                                sent: true,
                                received: true,
                              });
                              setStatusFilter({
                                delivered: true,
                                sent: true,
                                failed: true,
                                undelivered: true,
                                received: true,
                              });
                              setSearchQuery("");
                            }}
                            size="sm"
                            variant="outline"
                          >
                            Reset Filters
                          </Button>
                        </HStack>
                      </>
                    );
                  }

                  return (
                    <>
                      <PhoneIcon boxSize={12} color="gray.300" mb={4} />
                      <Heading size="md" color="gray.500" mb={2}>
                        No SMS Messages Yet
                      </Heading>
                      <Text color="gray.400">
                        SMS messages sent through the "Send SMS" and "Send
                        Forms" features will appear here.
                      </Text>
                    </>
                  );
                })()}
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Test Results */}
        {testResult && (
          <Card>
            <CardHeader>
              <Heading size="md">Connection Test Results</Heading>
            </CardHeader>
            <CardBody>
              <Alert
                status={testResult.success ? "success" : "error"}
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                height="200px"
                borderRadius="md"
              >
                <AlertIcon boxSize="40px" mr={0} />
                <AlertTitle mt={4} mb={1} fontSize="lg">
                  {testResult.success ? "Test Successful" : "Test Failed"}
                </AlertTitle>
                <AlertDescription maxWidth="sm">
                  {testResult.message}
                  {testResult.messageSid && (
                    <Box mt={2}>
                      <Text fontSize="sm" fontWeight="semibold">
                        Message SID:
                      </Text>
                      <Code fontSize="xs">{testResult.messageSid}</Code>
                    </Box>
                  )}
                  <Text fontSize="xs" mt={2} color="gray.500">
                    Tested at: {new Date(testResult.timestamp).toLocaleString()}
                  </Text>
                </AlertDescription>
              </Alert>
            </CardBody>
          </Card>
        )}

        {/* Health Issues */}
        {healthData.status === "error" && (
          <Card>
            <CardHeader>
              <Heading size="md">Health Issues Detected</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Twilio Service Issues</AlertTitle>
                    <AlertDescription>
                      The Twilio service is experiencing issues. Please check
                      your configuration and ensure your Twilio credentials are
                      valid.
                    </AlertDescription>
                  </Box>
                </Alert>

                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Troubleshooting Steps:
                  </Text>
                  <VStack align="start" spacing={2}>
                    <Text>• Verify your Twilio Account SID and Auth Token</Text>
                    <Text>
                      • Ensure your Twilio phone number is active and configured
                    </Text>
                    <Text>• Check your account billing and usage limits</Text>
                    <Text>
                      • Review Twilio service status at status.twilio.com
                    </Text>
                  </VStack>
                </Box>
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Resend Modal */}
      <Modal isOpen={isResendOpen} onClose={onResendClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Resend Message</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  value={resendPhoneNumber}
                  onChange={(e) => setResendPhoneNumber(e.target.value)}
                  placeholder="+1234567890"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Message Body</FormLabel>
                <Textarea
                  value={resendMessageBody}
                  onChange={(e) => setResendMessageBody(e.target.value)}
                  placeholder="Enter message..."
                  rows={6}
                />
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={shouldSplit}
                  onChange={(e) => setShouldSplit(e.target.checked)}
                >
                  Split message if too long (&gt;1600 characters)
                </Checkbox>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onResendClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleResendConfirm}
              isLoading={isResending}
              loadingText="Sending"
            >
              Send Message
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Sync Report Modal */}
      <Modal isOpen={isSyncReportOpen} onClose={onSyncReportClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Sync Report</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Successfully synchronized {syncReport.length} messages from
                Twilio.
              </Text>
              <TableContainer maxHeight="400px" overflowY="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Time</Th>
                      <Th>Action</Th>
                      <Th>Status</Th>
                      <Th>To</Th>
                      <Th>Error Code</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {syncReport.map((msg, index) => (
                      <Tr key={msg.sid || index}>
                        <Td>
                          {msg.created_at || msg.date_sent
                            ? new Date(
                                msg.created_at || msg.date_sent,
                              ).toLocaleString()
                            : "-"}
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={
                              msg.action === "inserted" ? "purple" : "blue"
                            }
                          >
                            {msg.action || "updated"}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={
                              msg.status === "delivered" ||
                              msg.status === "received" ||
                              msg.status === "sent"
                                ? "green"
                                : "red"
                            }
                          >
                            {msg.status}
                          </Badge>
                        </Td>
                        <Td>{msg.to}</Td>
                        <Td>{msg.error_code || "-"}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onSyncReportClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Message View Modal */}
      <Modal isOpen={isMessageViewOpen} onClose={onMessageViewClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex justify="space-between" align="center">
              <Text>Message Details</Text>
              <Button
                size="sm"
                leftIcon={<CopyIcon />}
                colorScheme="blue"
                variant="outline"
                onClick={handleCopyMessage}
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingMessage && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Time
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {new Date(
                      viewingMessage.timestamp || viewingMessage.createdAt,
                    ).toLocaleString()}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Direction
                  </Text>
                  <Badge
                    colorScheme={
                      viewingMessage.direction === "outbound" ? "blue" : "green"
                    }
                  >
                    {viewingMessage.direction === "outbound"
                      ? "📤 Sent"
                      : "📥 Received"}
                  </Badge>
                </Box>

                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Phone Number
                  </Text>
                  <Text fontSize="sm" fontFamily="mono" color="gray.600">
                    {viewingMessage.phoneNumber}
                    <PhoneStatusBadge phone={viewingMessage.phoneNumber} ml={2} fontFamily="body" />
                  </Text>
                </Box>

                {viewingMessage.patientName && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>
                      Patient
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      {viewingMessage.patientName}
                    </Text>
                  </Box>
                )}

                {!viewingMessage.patientName && viewingMessage.phoneNumber && patientLookup[normalizePhone(viewingMessage.phoneNumber)]?.name && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>
                      Patient
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      {patientLookup[normalizePhone(viewingMessage.phoneNumber)].name}
                    </Text>
                  </Box>
                )}

                {viewingMessage.patientId && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>
                      Patient ID
                    </Text>
                    <Text fontSize="sm" fontFamily="mono" color="gray.500">
                      {viewingMessage.patientId}
                    </Text>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Status
                  </Text>
                  <Badge
                    colorScheme={
                      viewingMessage.status === "delivered" ||
                      viewingMessage.status === "received" ||
                      viewingMessage.status === "Success"
                        ? "green"
                        : viewingMessage.status === "failed" ||
                            viewingMessage.status === "Failed" ||
                            viewingMessage.status === "undelivered"
                          ? "red"
                          : viewingMessage.status === "sent" ||
                              viewingMessage.status === "outbound"
                            ? "blue"
                            : "gray"
                    }
                    variant="subtle"
                  >
                    {viewingMessage.status}
                  </Badge>
                </Box>

                <Divider />

                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Message Content
                  </Text>
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    borderWidth={1}
                    borderColor="gray.200"
                  >
                    <Text
                      fontSize="sm"
                      whiteSpace="pre-wrap"
                      wordBreak="break-word"
                      color="gray.800"
                    >
                      {viewingMessage.fullMessage ||
                        viewingMessage.preview ||
                        "(no content)"}
                    </Text>
                  </Box>
                </Box>

                {viewingMessage.errorMessage && (
                  <Box>
                    <Text fontWeight="semibold" mb={2} color="red.600">
                      Error Details
                    </Text>
                    {viewingMessage.errorCode && (
                      <Text fontSize="sm" color="red.500" mb={1}>
                        Error Code: {viewingMessage.errorCode}
                      </Text>
                    )}
                    <Box
                      p={4}
                      bg="red.50"
                      borderRadius="md"
                      borderWidth={1}
                      borderColor="red.200"
                    >
                      <Text
                        fontSize="sm"
                        whiteSpace="pre-wrap"
                        wordBreak="break-word"
                        color="red.800"
                      >
                        {viewingMessage.errorMessage}
                      </Text>
                    </Box>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onMessageViewClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TwilioHealthStatus;
